import { PROJECT_STAGE_UUID_MAP } from 'cargo-chief-commons';
import { INPUT_TYPES } from '../../../shared/tabbed-table/constants';
import { AccountSettingsService } from '@service/accountSettings/account-settings.service';

export const PROJECT_FIELDS = {
	START_DATE: 'Start Date',
	ORIGIN_CITY: 'Origin City',
	ORIGIN_STATE: 'Origin State',
	DESTINATION_CITY: 'Destination City',
	DESTINATION_STATE: 'Destination State',
	CUSTOMER: 'Customer',
	VOLUME: 'Volume',
	EQUIPMENT: 'Equipment',
	COMMODITY: 'Commodity',
	WEIGHT: 'Weight',
	MAX_BUY: 'Max Buy',
	TARGET_CARRIER_RATE: 'Target Carrier Rate',
	OFFER_RATE: 'Offer Rate',
	ALLOCATED_CARRIERS: 'Allocated Carriers',
	ENGAGED_CARRIERS: 'Engaged Carriers',
	PROCURED_CARRIERS: 'Procured Carriers',
	ASSIGNED_REPS: 'Assigned Reps',
	ORIGIN: 'Origin',
	DESTINATION: 'Destination',
	STAGE: 'Stage',
	TYPE: 'Type',
	ARCHIVED: 'Archived',
	REVENUE_CODE: 'Revenue Code',
	LANE: 'Lane',
	LOADS: 'Loads',
	CARRIER: 'Carrier',
};

export function buildHeaders(accountSettings: AccountSettingsService) {
	let headers = [
		{ dataName: 'select', formattedName: 'select' },
		{ dataName: 'startDate', formattedName: 'Start Date' },
		{ dataName: 'lanePath', formattedName: PROJECT_FIELDS.LANE },
		{ dataName: 'revenueCode', formattedName: PROJECT_FIELDS.REVENUE_CODE },
		{ dataName: 'monthlyVolumeDisplay', formattedName: PROJECT_FIELDS.VOLUME },
		{ dataName: 'commodity', formattedName: PROJECT_FIELDS.COMMODITY },
		{ dataName: 'maxBuy', formattedName: PROJECT_FIELDS.MAX_BUY },
		{ dataName: 'offerRate', formattedName: PROJECT_FIELDS.OFFER_RATE },
		{ dataName: 'procuredCarriers', formattedName: PROJECT_FIELDS.PROCURED_CARRIERS },
		{ dataName: 'loadsCount', formattedName: PROJECT_FIELDS.LOADS },
		{ dataName: 'assignedReps', formattedName: PROJECT_FIELDS.ASSIGNED_REPS },
		{ dataName: 'archived', formattedName: PROJECT_FIELDS.ARCHIVED },
		{ dataName: 'type', formattedName: PROJECT_FIELDS.TYPE },
	];
	if (accountSettings.ROLE_BASED_CUSTOMER_DATA_ACCESS) {
		headers.splice(5, 0, { dataName: 'customerName', formattedName: PROJECT_FIELDS.CUSTOMER });
	}
	return headers;
}

export function buildHeadersForProcurement(accountSettings: AccountSettingsService) {
	let headers = [
		{ dataName: 'select', formattedName: 'select' },
		{ dataName: 'startDate', formattedName: 'Start Date' },
		{ dataName: 'lanePath', formattedName: PROJECT_FIELDS.LANE },
		{ dataName: 'revenueCode', formattedName: PROJECT_FIELDS.REVENUE_CODE },
		{ dataName: 'monthlyVolumeDisplay', formattedName: PROJECT_FIELDS.VOLUME },
		{ dataName: 'commodity', formattedName: PROJECT_FIELDS.COMMODITY },
		{ dataName: 'maxBuy', formattedName: PROJECT_FIELDS.MAX_BUY },
		{ dataName: 'offerRate', formattedName: PROJECT_FIELDS.OFFER_RATE },
		{ dataName: 'engagedCarriers', formattedName: PROJECT_FIELDS.ENGAGED_CARRIERS },
		{ dataName: 'procuredCarriers', formattedName: PROJECT_FIELDS.PROCURED_CARRIERS },
		{ dataName: 'assignedReps', formattedName: PROJECT_FIELDS.ASSIGNED_REPS },
		{ dataName: 'archived', formattedName: PROJECT_FIELDS.ARCHIVED },
	];
	if (accountSettings.ROLE_BASED_CUSTOMER_DATA_ACCESS) {
		headers.splice(5, 0, { dataName: 'customerName', formattedName: PROJECT_FIELDS.CUSTOMER });
	}
	return headers;
}

export function buildHeaderDataTypes(accountSettings: AccountSettingsService) {
	// NOTE: There is a known column type misalignment issue where types[5] is 'currency'
	// but displayedColumns[5] is 'commodity' (which should be 'string'). This misalignment
	// was identified in PR review but left unresolved because:
	// 1. Previous attempts to fix it caused DecimalPipe errors and broken page layouts
	// 2. The misalignment doesn't affect user functionality - actions work correctly
	// 3. The fix requires careful coordination across 3 different table configurations
	//    (Projects, Procurement, Workflows) with different column requirements
	// 4. The ROLE_BASED_CUSTOMER_DATA_ACCESS splice logic adds complexity to column alignment
	// TODO: Address this alignment issue in a future careful investigation when we can
	//       thoroughly test all three pages and their interactions
	let types = [
		'string',
		'string',
		'string',
		'string',
		'string',
		'currency', // This should be 'string' for 'commodity' but causes issues when changed
		'currency',
		'number',
		'number',
		'string',
		'string',
		'string',
	];
	if (accountSettings.ROLE_BASED_CUSTOMER_DATA_ACCESS) {
		types.splice(5, 0, 'string');
	}
	return types;
}

export function buildHeaderDataTypesForProcurement(accountSettings: AccountSettingsService) {
	// Indexed against headers.slice(1) — tabbed-table drops the 'select' column before
	// pairing headers with types (see renderedDataTypes in tabbed-table.component.ts).
	// Order: startDate, lanePath, revenueCode, monthlyVolumeDisplay, commodity, maxBuy,
	// offerRate, engagedCarriers, procuredCarriers, assignedReps, archived.
	let types = [
		'string', // startDate
		'string', // lanePath
		'string', // revenueCode
		'string', // monthlyVolumeDisplay
		'string', // commodity
		'currency', // maxBuy (stored in cents)
		'currency', // offerRate (stored in cents)
		'number', // engagedCarriers
		'number', // procuredCarriers
		'string', // assignedReps
		'string', // archived
	];
	if (accountSettings.ROLE_BASED_CUSTOMER_DATA_ACCESS) {
		// customerName is spliced into headers between monthlyVolumeDisplay and commodity
		types.splice(4, 0, 'string');
	}
	return types;
}

export function buildDisplayedColumns(accountSettings: AccountSettingsService) {
	let columns = [
		'select',
		'startDate',
		'lanePath',
		'revenueCode',
		'monthlyVolumeDisplay',
		'commodity',
		'maxBuy',
		'offerRate',
		'procuredCarriers',
		'loadsCount',
		'assignedReps',
		'type',
	];
	if (accountSettings.ROLE_BASED_CUSTOMER_DATA_ACCESS) {
		columns.splice(5, 0, 'customerName');
	}
	return columns;
}

export function buildDisplayedColumnsForProcurement(accountSettings: AccountSettingsService) {
	let columns = [
		'select',
		'startDate',
		'lanePath',
		'revenueCode',
		'monthlyVolumeDisplay',
		'commodity',
		'maxBuy',
		'offerRate',
		'engagedCarriers',
		'procuredCarriers',
		'assignedReps',
	];
	if (accountSettings.ROLE_BASED_CUSTOMER_DATA_ACCESS) {
		columns.splice(5, 0, 'customerName');
	}
	return columns;
}

// Workflow-specific functions (includes Allocated Carriers, excludes Type)
export function buildHeadersForWorkflows(accountSettings: AccountSettingsService) {
	let headers = [
		{ dataName: 'select', formattedName: 'select' },
		{ dataName: 'startDate', formattedName: 'Start Date' },
		{ dataName: 'lanePath', formattedName: PROJECT_FIELDS.LANE },
		{ dataName: 'revenueCode', formattedName: PROJECT_FIELDS.REVENUE_CODE },
		{ dataName: 'monthlyVolumeDisplay', formattedName: PROJECT_FIELDS.VOLUME },
		{ dataName: 'commodity', formattedName: PROJECT_FIELDS.COMMODITY },
		{ dataName: 'maxBuy', formattedName: PROJECT_FIELDS.MAX_BUY },
		{ dataName: 'offerRate', formattedName: PROJECT_FIELDS.OFFER_RATE },
		{ dataName: 'allocatedCarriers', formattedName: PROJECT_FIELDS.ALLOCATED_CARRIERS },
		{ dataName: 'procuredCarriers', formattedName: PROJECT_FIELDS.PROCURED_CARRIERS },
		{ dataName: 'loadsCount', formattedName: PROJECT_FIELDS.LOADS },
		{ dataName: 'assignedReps', formattedName: PROJECT_FIELDS.ASSIGNED_REPS },
		{ dataName: 'archived', formattedName: PROJECT_FIELDS.ARCHIVED },
	];
	if (accountSettings.ROLE_BASED_CUSTOMER_DATA_ACCESS) {
		headers.splice(5, 0, { dataName: 'customerName', formattedName: PROJECT_FIELDS.CUSTOMER });
	}
	return headers;
}

export function buildHeaderDataTypesForWorkflows(accountSettings: AccountSettingsService) {
	// Types must align with displayedColumnsForWorkflows AFTER slice(1) removes 'select'
	// The template iterates headers.slice(1) when rowSelectableEnabled, so types should NOT
	// include an entry for 'select'. Order: startDate, lanePath, revenueCode, monthlyVolumeDisplay,
	// commodity, maxBuy, offerRate, allocatedCarriers, procuredCarriers, loadsCount, assignedReps
	let types = [
		'string',    // startDate
		'string',    // lanePath
		'string',    // revenueCode
		'string',    // monthlyVolumeDisplay
		'string',    // commodity
		'currency',  // maxBuy (stored in cents)
		'currency',  // offerRate (stored in cents)
		'number',    // allocatedCarriers
		'number',    // procuredCarriers
		'string',    // loadsCount
		'string',    // assignedReps
	];
	if (accountSettings.ROLE_BASED_CUSTOMER_DATA_ACCESS) {
		// Insert 'string' for customerName at index 4 (between monthlyVolumeDisplay and commodity)
		types.splice(4, 0, 'string');
	}
	return types;
}

export function buildDisplayedColumnsForWorkflows(accountSettings: AccountSettingsService) {
	let columns = [
		'select',
		'startDate',
		'lanePath',
		'revenueCode',
		'monthlyVolumeDisplay',
		'commodity',
		'maxBuy',
		'offerRate',
		'allocatedCarriers',
		'procuredCarriers',
		'loadsCount',
		'assignedReps',
	];
	if (accountSettings.ROLE_BASED_CUSTOMER_DATA_ACCESS) {
		columns.splice(5, 0, 'customerName');
	}
	return columns;
}

export function buildSortFields(accountSettings: AccountSettingsService) {
	let sortFields = [
		PROJECT_FIELDS.START_DATE,
		PROJECT_FIELDS.ORIGIN_CITY,
		PROJECT_FIELDS.ORIGIN_STATE,
		PROJECT_FIELDS.DESTINATION_CITY,
		PROJECT_FIELDS.DESTINATION_STATE,
		PROJECT_FIELDS.VOLUME,
		PROJECT_FIELDS.EQUIPMENT,
		PROJECT_FIELDS.COMMODITY,
		PROJECT_FIELDS.MAX_BUY,
		PROJECT_FIELDS.OFFER_RATE,
		PROJECT_FIELDS.PROCURED_CARRIERS,
		PROJECT_FIELDS.ASSIGNED_REPS,
		PROJECT_FIELDS.STAGE,
		PROJECT_FIELDS.TYPE,
	];
	if (accountSettings.ROLE_BASED_CUSTOMER_DATA_ACCESS) {
		sortFields.splice(5, 0, PROJECT_FIELDS.CUSTOMER);
	}
	return sortFields;
}

export function buildFilerFields(
	accountSettings: AccountSettingsService,
	isUserRoleAdmin: boolean = true,
	skipTypeFilter: boolean = false
) {
	let filters = [
		PROJECT_FIELDS.START_DATE,
		PROJECT_FIELDS.ORIGIN_CITY,
		PROJECT_FIELDS.ORIGIN_STATE,
		PROJECT_FIELDS.DESTINATION_CITY,
		PROJECT_FIELDS.DESTINATION_STATE,
		PROJECT_FIELDS.VOLUME,
		PROJECT_FIELDS.EQUIPMENT,
		PROJECT_FIELDS.COMMODITY,
		PROJECT_FIELDS.MAX_BUY,
		PROJECT_FIELDS.OFFER_RATE,
		PROJECT_FIELDS.PROCURED_CARRIERS,
		PROJECT_FIELDS.ASSIGNED_REPS,
		PROJECT_FIELDS.STAGE,
		PROJECT_FIELDS.ARCHIVED,
		PROJECT_FIELDS.TYPE,
	];
	// Remove ASSIGNED_REPS filter field if NON_ADMIN_SEE_OWN_LOADS_ONLY is enabled and user is not admin
	if (accountSettings.NON_ADMIN_SEE_OWN_LOADS_ONLY && !isUserRoleAdmin) {
		filters = filters.filter((field) => field !== PROJECT_FIELDS.ASSIGNED_REPS);
	}
	if (accountSettings.ROLE_BASED_CUSTOMER_DATA_ACCESS) {
		filters.splice(5, 0, PROJECT_FIELDS.CUSTOMER);
	}
	if (skipTypeFilter) {
		filters = filters.filter((field) => field !== PROJECT_FIELDS.TYPE);
	}
	return filters;
}

export function buildFilterFieldsForWorkflows(
	accountSettings: AccountSettingsService,
	isUserRoleAdmin: boolean = true
) {
	let filters = [
		PROJECT_FIELDS.START_DATE,
		PROJECT_FIELDS.ORIGIN_CITY,
		PROJECT_FIELDS.ORIGIN_STATE,
		PROJECT_FIELDS.DESTINATION_CITY,
		PROJECT_FIELDS.DESTINATION_STATE,
		PROJECT_FIELDS.VOLUME,
		PROJECT_FIELDS.EQUIPMENT,
		PROJECT_FIELDS.COMMODITY,
		PROJECT_FIELDS.MAX_BUY,
		PROJECT_FIELDS.OFFER_RATE,
		PROJECT_FIELDS.ALLOCATED_CARRIERS,
		PROJECT_FIELDS.PROCURED_CARRIERS,
		PROJECT_FIELDS.ASSIGNED_REPS,
		PROJECT_FIELDS.STAGE,
		PROJECT_FIELDS.ARCHIVED,
	];

	// Remove ASSIGNED_REPS filter field if NON_ADMIN_SEE_OWN_LOADS_ONLY is enabled and user is not admin
	if (accountSettings.NON_ADMIN_SEE_OWN_LOADS_ONLY && !isUserRoleAdmin) {
		filters = filters.filter((field) => field !== PROJECT_FIELDS.ASSIGNED_REPS);
	}

	if (accountSettings.ROLE_BASED_CUSTOMER_DATA_ACCESS) {
		filters.splice(5, 0, PROJECT_FIELDS.CUSTOMER);
	}

	return filters;
}

export const SORT_FIELD_MAPS = {
	[PROJECT_FIELDS.START_DATE]: 'dueDate',
	[PROJECT_FIELDS.ORIGIN_CITY]: 'originCity',
	[PROJECT_FIELDS.ORIGIN_STATE]: 'originState',
	[PROJECT_FIELDS.DESTINATION_CITY]: 'destCity',
	[PROJECT_FIELDS.DESTINATION_STATE]: 'destState',
	[PROJECT_FIELDS.CUSTOMER]: 'customerName',
	[PROJECT_FIELDS.VOLUME]: 'volume',
	[PROJECT_FIELDS.EQUIPMENT]: 'equipmentType',
	[PROJECT_FIELDS.COMMODITY]: 'commodity',
	[PROJECT_FIELDS.MAX_BUY]: 'maxBuy',
	[PROJECT_FIELDS.OFFER_RATE]: 'offerRate',
	[PROJECT_FIELDS.ALLOCATED_CARRIERS]: 'allocatedCarriersCount',
	[PROJECT_FIELDS.PROCURED_CARRIERS]: 'carrierCount',
	[PROJECT_FIELDS.ASSIGNED_REPS]: 'assigneeIds',
	[PROJECT_FIELDS.STAGE]: 'stageIds',
	[PROJECT_FIELDS.TYPE]: 'type',
};

export let filterFieldsInputTypeMap: Map<string, string> = new Map<string, string>();

filterFieldsInputTypeMap.set(PROJECT_FIELDS.START_DATE, INPUT_TYPES.DATE);
filterFieldsInputTypeMap.set(PROJECT_FIELDS.ORIGIN_CITY, INPUT_TYPES.LOCATION);
filterFieldsInputTypeMap.set(PROJECT_FIELDS.ORIGIN_STATE, INPUT_TYPES.STATE);
filterFieldsInputTypeMap.set(PROJECT_FIELDS.DESTINATION_CITY, INPUT_TYPES.LOCATION);
filterFieldsInputTypeMap.set(PROJECT_FIELDS.DESTINATION_STATE, INPUT_TYPES.STATE);

filterFieldsInputTypeMap.set(PROJECT_FIELDS.CUSTOMER, INPUT_TYPES.CUSTOMER);
filterFieldsInputTypeMap.set(PROJECT_FIELDS.VOLUME, INPUT_TYPES.VOLUME);
filterFieldsInputTypeMap.set(PROJECT_FIELDS.EQUIPMENT, INPUT_TYPES.TRAILER_TYPE);
filterFieldsInputTypeMap.set(PROJECT_FIELDS.COMMODITY, INPUT_TYPES.STRING);
filterFieldsInputTypeMap.set(PROJECT_FIELDS.MAX_BUY, INPUT_TYPES.NUMBER);
filterFieldsInputTypeMap.set(PROJECT_FIELDS.TARGET_CARRIER_RATE, INPUT_TYPES.NUMBER);
filterFieldsInputTypeMap.set(PROJECT_FIELDS.PROCURED_CARRIERS, INPUT_TYPES.NUMBER);
filterFieldsInputTypeMap.set(PROJECT_FIELDS.ALLOCATED_CARRIERS, INPUT_TYPES.ALLOCATED_CARRIERS);
filterFieldsInputTypeMap.set(PROJECT_FIELDS.ASSIGNED_REPS, INPUT_TYPES.USERS_LIST);
filterFieldsInputTypeMap.set(PROJECT_FIELDS.STAGE, INPUT_TYPES.STAGE);
filterFieldsInputTypeMap.set(PROJECT_FIELDS.ARCHIVED, INPUT_TYPES.BOOLEAN);
filterFieldsInputTypeMap.set(PROJECT_FIELDS.TYPE, INPUT_TYPES.PROJECT_TYPE);

export const PROJECT_STAGES = [
	{
		label: 'Awarded',
		type: PROJECT_STAGE_UUID_MAP.AWARDED,
	},
	{
		label: 'On Hold',
		type: PROJECT_STAGE_UUID_MAP.ON_HOLD,
	},
	{
		label: 'Re Bid',
		type: PROJECT_STAGE_UUID_MAP.RE_BID,
	},
	{
		label: 'New',
		type: PROJECT_STAGE_UUID_MAP.NEW,
	},
	{
		label: 'Live',
		type: PROJECT_STAGE_UUID_MAP.LIVE,
	},
	{
		label: 'Submitted',
		type: PROJECT_STAGE_UUID_MAP.SUBMITTED,
	},
];

export const DISABLED_PROJECT_TOOLTIP =
	'This project is currently disabled and will not be used by the Booking Assistant. To enable, select the project and go to the Actions menu.';
